import React, { ReactElement } from 'react'
import { Box, Typography, Paper, Chip, CircularProgress } from '@mui/material'
import {
  Psychology as PlanningIcon,
  Settings as ExecutionIcon,
  CheckCircle as CompleteIcon,
  Error as ErrorIcon,
  Analytics as AnalysisIcon,
  Description as DefaultIcon,
} from '@mui/icons-material'
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab'
import { useAppSelector } from 'src/store/hooks'
import dayjs from 'dayjs'
import { selectMotherSessionDetail } from '../mother.slice'

type TimelineTabProps = {
  correlationID: string
}

let getPhaseColor = (
  phase: string,
): 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' => {
  if (!phase) return 'primary'
  switch (phase.toLowerCase()) {
    case 'planning':
      return 'info'
    case 'execution':
      return 'primary'
    case 'complete':
    case 'completed':
      return 'success'
    case 'failed':
    case 'error':
      return 'error'
    case 'analysis':
      return 'secondary'
    default:
      return 'primary'
  }
}

let getPhaseIcon = (phase: string) => {
  if (!phase) return <DefaultIcon fontSize="small" />
  switch (phase.toLowerCase()) {
    case 'planning':
      return <PlanningIcon fontSize="small" />
    case 'execution':
      return <ExecutionIcon fontSize="small" />
    case 'complete':
    case 'completed':
      return <CompleteIcon fontSize="small" />
    case 'failed':
    case 'error':
      return <ErrorIcon fontSize="small" />
    case 'analysis':
      return <AnalysisIcon fontSize="small" />
    default:
      return <DefaultIcon fontSize="small" />
  }
}

export default ({ correlationID }: TimelineTabProps): ReactElement => {
  let sessionDetail = useAppSelector(selectMotherSessionDetail(correlationID))
  let timeline = sessionDetail?.progress || []

  if (!sessionDetail) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 200,
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (!timeline || timeline.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
        <Typography>No timeline data available</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Session Timeline
      </Typography>

      <Paper sx={{ p: 2, bgcolor: 'background.paperDark' }}>
        <Timeline role="list" aria-label="Session timeline events">
          {timeline.map((event: any, index: number) => (
            <TimelineItem key={index} role="listitem">
              <TimelineOppositeContent
                sx={{ m: 'auto 0', minWidth: 120 }}
                align="right"
                variant="body2"
                color="text.secondary"
              >
                {dayjs(event.timestamp).format('h:mm:ss A')}
                <br />
                <Typography variant="caption" color="text.disabled">
                  {dayjs(event.timestamp).format('MMM D')}
                </Typography>
              </TimelineOppositeContent>

              <TimelineSeparator>
                <TimelineDot
                  color={getPhaseColor(event.phase)}
                  aria-label={`${event.phase} phase`}
                >
                  <span style={{ fontSize: '12px' }}>
                    {getPhaseIcon(event.phase)}
                  </span>
                </TimelineDot>
                {index < timeline.length - 1 && <TimelineConnector />}
              </TimelineSeparator>

              <TimelineContent sx={{ py: '12px', px: 2 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Chip
                    label={event.phase}
                    color={getPhaseColor(event.phase)}
                    size="small"
                    aria-label={`Phase: ${event.phase}`}
                    variant="outlined"
                  />
                </Box>

                <Typography
                  variant="body1"
                  sx={{ fontWeight: 'medium' }}
                  gutterBottom
                >
                  {event.message}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {dayjs(event.timestamp).format('MMM D, YYYY h:mm:ss A')}
                </Typography>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </Paper>
    </Box>
  )
}
